// lib/insights.js — build-time data access for the Insights section.
//
// Content is baked into the static export at build time (real per-URL HTML,
// readable by crawlers that don't execute JS). Publishing a new insight now
// means editing data/insights.json and redeploying — the previous "no
// redeploy needed" live-fetch (insights-feed.jsx) traded that for SEO
// invisibility, since the manifest was only ever fetched client-side after
// mount and every insight had no page of its own to be cited from.

import raw from '@/data/insights.json';

export const POSTS_PER_PAGE = 12;

const TONES = ['studio', 'industrial', 'sky', 'earth', 'plant'];

// insights.json stores human dates ("March 2026"). Article/NewsArticle
// structured data needs ISO 8601 — parse loosely, fall back to null (field
// omitted) rather than guessing.
function toIsoDate(human) {
  if (!human) return null;
  const parsed = new Date(`1 ${human}`);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString().slice(0, 10);
}

function slugifyCategory(category) {
  return category.toLowerCase().replace(/&/g, '-and-').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function normalize(it, i) {
  return {
    id: it.id || `i${i + 1}`,
    slug: it.slug || (it.id || `insight-${i + 1}`),
    kind: it.kind || 'Insight',
    category: it.category || it.kind || 'Insight',
    title: it.title || 'Untitled',
    date: it.date || '',
    dateISO: toIsoDate(it.date),
    blurb: it.blurb || '',
    featuredBlurb: it.featuredBlurb || it.excerpt || it.blurb || '',
    // Full post body, as an array of blocks — a plain string is a paragraph,
    // a string starting with "## " is a subheading. Absent for the older,
    // teaser-only entries (they render blurb-only, same as before).
    body: Array.isArray(it.body) ? it.body : null,
    tone: it.tone || TONES[i % TONES.length],
    image: it.image || it.img || null,
    href: it.href || it.url || it.document || null,
    featured: !!it.featured,
  };
}

export function getAllInsights() {
  return (raw.insights || []).map(normalize);
}

export function getInsightBySlug(slug) {
  return getAllInsights().find((it) => it.slug === slug) || null;
}

// { name, slug, count } for every distinct category, most-populated first.
export function getCategories() {
  const counts = new Map();
  for (const it of getAllInsights()) {
    counts.set(it.category, (counts.get(it.category) || 0) + 1);
  }
  return [...counts.entries()]
    .map(([name, count]) => ({ name, slug: slugifyCategory(name), count }))
    .sort((a, b) => b.count - a.count);
}

export function getInsightsByCategorySlug(categorySlug) {
  return getAllInsights().filter((it) => slugifyCategory(it.category) === categorySlug);
}

export function getCategoryBySlug(categorySlug) {
  return getCategories().find((c) => c.slug === categorySlug) || null;
}

// Slices `items` to one page (1-indexed) of POSTS_PER_PAGE, and returns the
// slice plus enough info to render pagination controls / generateStaticParams.
export function paginate(items, page = 1) {
  const totalPages = Math.max(1, Math.ceil(items.length / POSTS_PER_PAGE));
  const current = Math.min(Math.max(1, page), totalPages);
  const start = (current - 1) * POSTS_PER_PAGE;
  return {
    items: items.slice(start, start + POSTS_PER_PAGE),
    page: current,
    totalPages,
    hasPrev: current > 1,
    hasNext: current < totalPages,
  };
}
